import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_CLIP_TITLES, GET_MY_CLIP } from '@/queries/queries';
import type { CreateClipTitlesMutation, CreateClipTitlesMutationVariables, MyClipQuery, MyClipQueryVariables } from '@/generated/graphql';
import { useTranslation } from 'react-i18next';

interface ClipTitlesData {
  tags?: string;
  thumb_list?: string[];
  title_list?: string[];
  description?: string;
}

interface UseClipTitlesReturn {
  titlesData: ClipTitlesData | null;
  isGenerating: boolean;
  isPolling: boolean;
  error: string | null;
  generateTitles: () => Promise<void>;
  refetch: () => void;
}

export function useClipTitles(clipId: string, existingCopy?: any): UseClipTitlesReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [titlesData, setTitlesData] = useState<ClipTitlesData | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const maxAttemptsRef = useRef(0);
  const { t } = useTranslation();

  const [createClipTitlesMutation] = useMutation<CreateClipTitlesMutation, CreateClipTitlesMutationVariables>(
    CREATE_CLIP_TITLES
  );

  // Query to poll for clip updates - specifically for copy generation
  const { data: polledClip, refetch } = useQuery<MyClipQuery, MyClipQueryVariables>(
    GET_MY_CLIP,
    {
      variables: { id: clipId },
      skip: !isPolling,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  // Setup polling interval when needed
  useEffect(() => {
    if (!isPolling) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Start polling
    intervalRef.current = setInterval(() => {
      maxAttemptsRef.current++;

      // Stop polling after 60 attempts (3 minutes at 3-second intervals)
      // if (maxAttemptsRef.current >= 60) {
      //   setError('Tempo limite excedido para geração de títulos');
      //   setIsPolling(false);
      //   return;
      // }

      refetch();
    }, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling, refetch]);

  // Handle copy status changes from polled data
  useEffect(() => {
    if (!isPolling) return;

    const copy = polledClip?.myClip?.copy;
    if (!copy) return;

    if (copy.result) {
      setTitlesData(copy.result);
      setIsPolling(false);
      maxAttemptsRef.current = 0;
    } else if (copy.processing_status === 'failure') {
      setError(t('clipTitlesHook.generationFailed'));
      setIsPolling(false);
      maxAttemptsRef.current = 0;
    }
  }, [polledClip, isPolling]);

  // Initialize titles data from existing copy
  useEffect(() => {
    if (existingCopy?.processing_status === 'success' || existingCopy?.result) {
      setTitlesData(existingCopy.result);
    } else if (existingCopy?.processing_status === 'processing' || existingCopy?.processing_status === 'queued') {
      setIsPolling(true);
    } else if (existingCopy?.processing_status === 'failure') {
      setError(t('clipTitlesHook.generationFailed'));
    }
  }, [existingCopy]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const generateTitles = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      setTitlesData(null);
      maxAttemptsRef.current = 0;

      const result = await createClipTitlesMutation({
        variables: { id: clipId },
        refetchQueries: [{ query: GET_MY_CLIP, variables: { id: clipId } }]
      });

      if (result.data?.createCopies.copy) {
        const copy = result.data.createCopies.copy;

        setTitlesData(null);
        if (copy.processing_status === 'processing' || copy.processing_status === 'queued') {
          setIsPolling(true);
        } else if (copy.processing_status === 'success' || copy.result) {
          setTitlesData(copy.result);
        } else if (copy.processing_status === 'failure') {
          setError(t('clipTitlesHook.generationFailed'));
        }
      }
    } catch (err) {
      setError(t('clipTitlesHook.generationRequestError'));
      console.error('Error generating clip titles:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    titlesData,
    isGenerating,
    isPolling,
    error,
    generateTitles,
    refetch
  };
}
